import { readPrompt, type PromptPair } from '@/features/editor/lib/imagePrompt'
import type { CourseWithoutBlocks } from '@/types/course'
import type { Lesson } from '@/types/lesson'
import type { Block } from '@/types/blocks'
import { bannerKey, blockKey, elementKey, lessonKey, mascotKey, slotLang } from './targetKeys'
import type { ImageSlot } from './targetKeys'

/** Место, которое можно отправить в план: ключ, признак «уже нарисовано» и
 *  признак «сервер это место пропустит» (§6 — нет картинки-референса исходного
 *  слота у цели второго слота). */
export interface TargetRef {
  key: string
  hasImage: boolean
  willSkip: boolean
}

/**
 * Что собираем: колонку `slot` — при том, что оригинал курса лежит в
 * `sourceSlot`. Второй слот НЕ равен строке `'en'`: у английского курса
 * оригинал в колонке `*En`, и вторым слотом у него оказывается `base`.
 * Считает `sourceSlot` вызывающий — из языка оригинала (`seedLangFor`).
 */
export interface SlotPlan {
  slot: ImageSlot
  sourceSlot: ImageSlot
}

const isSecondSlot = (plan: SlotPlan) => plan.slot !== plan.sourceSlot

/** Значение той колонки, которую называет слот. */
const inSlot = (slot: ImageSlot, base: string | null | undefined, en: string | null | undefined) =>
  slot === 'base' ? base : en

const hasPrompt = (obj: PromptPair, slot: ImageSlot) =>
  readPrompt(obj, slotLang(slot)).trim() !== ''

const drawn = (url: string | null | undefined) => Boolean(url)

/**
 * Признак будущего пропуска (§6): у цели ВТОРОГО слота нет картинки исходного
 * слота — референса для перерисовки, — и сервер такую цель пропускает, денег
 * не беря. В исходном слоте пропуска не бывает: референс там не нужен.
 */
const skipsWithoutSource = (
  plan: SlotPlan,
  base: string | null | undefined,
  en: string | null | undefined,
) => isSecondSlot(plan) && !drawn(inSlot(plan.sourceSlot, base, en))

/**
 * Баннер и маскоты. Слота у них НЕТ ни в ключе, ни в данных (промпт один), и
 * второго языка у этих картинок не бывает — во ВТОРОМ слоте не предлагаем их
 * вовсе, иначе автор платил бы за перерисовку той же самой картинки.
 */
export const courseTargets = (
  course: CourseWithoutBlocks,
  plan: SlotPlan,
): { banner: TargetRef | null; mascots: TargetRef[] } => {
  if (isSecondSlot(plan)) return { banner: null, mascots: [] }
  const banner = (course.bannerPrompt ?? '').trim()
    ? { key: bannerKey(course.id), hasImage: drawn(course.banner), willSkip: false }
    : null
  const mascots = (course.mascots ?? [])
    .filter((m) => (m.imagePrompt ?? '').trim() !== '')
    .map((m) => ({ key: mascotKey(m.id), hasImage: drawn(m.image), willSkip: false }))
  return { banner, mascots }
}

const elementTargets = (block: Block, plan: SlotPlan): TargetRef[] =>
  block.type === 'design'
    ? block.elements
        // Пустой id — не цель: ключ `element:440:base:` бэк не опознает и
        // отобьёт `422` ВЕСЬ план, а перечитывание блоков не поможет —
        // элемент вернётся ровно таким же (контракт `designBlock.ts`).
        .filter((el) => el.type === 'image' && el.id.trim() !== '' && hasPrompt(el, plan.slot))
        .map((el) => {
          const url = el.type === 'image' ? el.imageUrl : ''
          const urlEn = el.type === 'image' ? el.imageUrlEn : ''
          return {
            key: elementKey(block.id, plan.slot, el.id),
            hasImage: drawn(inSlot(plan.slot, url, urlEn)),
            willSkip: skipsWithoutSource(plan, url, urlEn),
          }
        })
    : []

const blockTargets = (block: Block, plan: SlotPlan): TargetRef[] => {
  const own =
    'imagePrompt' in block && hasPrompt(block, plan.slot)
      ? [
          {
            key: blockKey(block.id, plan.slot),
            hasImage: drawn(inSlot(plan.slot, block.image, block.imageEn)),
            willSkip: skipsWithoutSource(plan, block.image, block.imageEn),
          },
        ]
      : []
  return [...own, ...elementTargets(block, plan)]
}

/**
 * Цели одного урока: сам урок, его блоки и картиночные элементы дизайна.
 *
 * Урок принимается БЕЗ блоков (`Omit<Lesson, 'blocks'>` — общая манера
 * редактора): деталь курса отдаёт уроки именно такими, а блоки приезжают
 * отдельным запросом строки и приходят вторым аргументом.
 */
export const lessonTargets = (
  lesson: Omit<Lesson, 'blocks'>,
  blocks: Block[],
  plan: SlotPlan,
): TargetRef[] => {
  const own = hasPrompt(lesson, plan.slot)
    ? [
        {
          key: lessonKey(lesson.id, plan.slot),
          hasImage: drawn(inSlot(plan.slot, lesson.image, lesson.imageEn)),
          willSkip: skipsWithoutSource(plan, lesson.image, lesson.imageEn),
        },
      ]
    : []
  return [...own, ...blocks.flatMap((block) => blockTargets(block, plan))]
}
